'use client'

import React from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { Accordion, ButtonLink, ReferralJourney } from '@kiao-voice/ui-kit'

/**
 * Public, SEO-indexable referral marketing page (`/referral`). The single source
 * of truth for the program's how-it-works steps + FAQ — the in-app referral
 * page (web + desktop) links here instead of duplicating that copy. Navbar +
 * footer come from the (marketing) layout. Signed-out CTA points at
 * `/app/referral`, which opens the sign-in modal in place via AuthGuard.
 */
export function ReferralPublicPage() {
  const { t } = useTranslation('web')
  const steps = [
    { n: '01', title: t('referralPublic.step1Title'), body: t('referralPublic.step1Body') },
    { n: '02', title: t('referralPublic.step2Title'), body: t('referralPublic.step2Body') },
    { n: '03', title: t('referralPublic.step3Title'), body: t('referralPublic.step3Body') },
  ]
  const faqs = [
    { value: 'eligibility', q: t('referralPublic.faq1Q'), a: t('referralPublic.faq1A') },
    { value: 'reward', q: t('referralPublic.faq2Q'), a: t('referralPublic.faq2A') },
    { value: 'tracking', q: t('referralPublic.faq3Q'), a: t('referralPublic.faq3A') },
    { value: 'limits', q: t('referralPublic.faq4Q'), a: t('referralPublic.faq4A') },
  ]

  return (
    <div className="refpub">
      {/* hero */}
      <section className="refpub-hero">
        <p className="eyebrow">{t('referralPublic.heroEyebrow')}</p>
        <h1>{t('referralPublic.heroTitle')}</h1>
        <p className="sub">{t('referralPublic.heroSub')}</p>
        <div className="cta">
          <ButtonLink as={Link} href="/app/referral">
            {t('referralPublic.ctaLogin')}
          </ButtonLink>
          <ButtonLink as={Link} variant="secondary" href="/pricing">
            {t('referralPublic.ctaPlans')}
          </ButtonLink>
        </div>
        <p className="fine">{t('referralPublic.heroFine')}</p>
      </section>

      {/* 運作方式 ink band */}
      <section className="refpub-band">
        <div className="in">
          <p className="kicker">{t('referralPublic.howKicker')}</p>
          <h2>{t('referralPublic.howTitle')}</h2>
          <ReferralJourney />
          <div className="refpub-steps">
            {steps.map((step) => (
              <div className="refpub-step" key={step.n}>
                <div className="n">{step.n}</div>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ — shared Accordion keeps one answer open while all copy remains in the DOM. */}
      <section className="refpub-faq-sec">
        <h2>{t('referralPublic.faqTitle')}</h2>
        <Accordion
          className="refpub-faq"
          defaultValue="eligibility"
          items={faqs.map((faq) => ({
            value: faq.value,
            title: faq.q,
            content: <p>{faq.a}</p>,
          }))}
        />
      </section>

      {/* 結尾 CTA */}
      <section className="refpub-endcta">
        <h2>{t('referralPublic.endTitle')}</h2>
        <ButtonLink as={Link} href="/app/referral">
          {t('referralPublic.ctaLogin')}
        </ButtonLink>
      </section>
    </div>
  )
}
